/**
 * Dubowitz Score (Dubowitz, Dubowitz & Goldberg 1970) — penilaian maturitas
 * neonatus untuk estimasi usia kehamilan.
 *
 * Total skor = subtotal neurologis (10 item) + subtotal eksternal/fisik (11 item).
 * Konversi skor → usia gestasi memakai persamaan regresi linear Dubowitz:
 *   usia (minggu) = 0.2642 × total_skor + 24.595 (dibulatkan; clamp 20–44).
 */

export interface DubowitzItem {
  key: string;
  label: string;
  min: number;
  max: number;
  hint: string;
}

export interface DubowitzScores {
  neuro: Record<string, number | null>;
  fisik: Record<string, number | null>;
}

export const DUBOWITZ_NEURO_ITEMS: DubowitzItem[] = [
  { key: "sikap_posture", label: "Sikap (Posture)", min: 0, max: 4, hint: "0=ekstensi penuh, 4=fleksi penuh" },
  { key: "sudut_radiokarpal", label: "Sudut Radiokarpal (Square Window)", min: 0, max: 4, hint: "0= >90°, 1=90°, 2=60°, 3=45°, 4=30°" },
  { key: "arm_recoil", label: "Arm Recoil", min: 0, max: 4, hint: "0=tidak ada recoil, 4=recoil cepat <90°" },
  { key: "dorsofleksi_tumit", label: "Dorsofleksi Tumit", min: 0, max: 4, hint: "0=180°, 4= <90°" },
  { key: "sudut_poplitea", label: "Sudut Poplitea", min: 0, max: 5, hint: "0=180°, 5= <90°" },
  { key: "leg_recoil", label: "Leg Recoil", min: 0, max: 2, hint: "0=tidak ada, 2=recoil cepat" },
  { key: "heel_to_ear", label: "Heel to Ear", min: 0, max: 4, hint: "0=tumit mudah ke telinga, 4=tidak bisa" },
  { key: "scarf_sign", label: "Scarf Sign", min: 0, max: 3, hint: "0=siku lewat garis tengah mudah, 3=tidak sampai" },
  { key: "head_lag", label: "Head-lag", min: 0, max: 3, hint: "0=lag total, 3=kepala stabil" },
  { key: "ventral_suspension", label: "Ventral Suspension", min: 0, max: 4, hint: "0=ekstensi penuh, 4=fleksi penuh" }
];

export const DUBOWITZ_FISIK_ITEMS: DubowitzItem[] = [
  { key: "udem", label: "Udem", min: 0, max: 2, hint: "0=udem jelas, 2=tidak ada udem" },
  { key: "elastisitas_kulit", label: "Elastisitas Kulit", min: 0, max: 4, hint: "0=sangat tipis, 4=tebal/kasar" },
  { key: "warna_kulit", label: "Warna Kulit", min: 0, max: 4, hint: "0=merah gelap, 4=pucat" },
  { key: "venae_abdomen", label: "Venae Abdomen", min: 0, max: 4, hint: "0=sangat terlihat, 4=tidak terlihat" },
  { key: "lanugo", label: "Lanugo", min: 0, max: 4, hint: "0=tidak ada, 2=tebal, 4=hampir hilang" },
  { key: "lipat_plantar", label: "Lipat Plantar", min: 0, max: 4, hint: "0=tidak ada, 4=lipatan seluruh telapak" },
  { key: "papila_mamae", label: "Papila Mamae", min: 0, max: 3, hint: "0=hampir tidak terlihat, 3=areola penuh" },
  { key: "kelenjar_mamae", label: "Kelenjar Mamae", min: 0, max: 3, hint: "0=tidak teraba, 3= >10mm" },
  { key: "bentuk_telinga", label: "Bentuk Telinga", min: 0, max: 3, hint: "0=datar, 3=terbentuk sempurna" },
  { key: "konsistensi_telinga", label: "Konsistensi Telinga", min: 0, max: 3, hint: "0=pinna lunak, 3=kartilago kaku" },
  { key: "organ_genitalia", label: "Organ Genitalia", min: 0, max: 2, hint: "0=imatur, 2=matur" }
];

const SLOPE = 0.2642;
const INTERCEPT = 24.595;
const MIN_WEEKS = 20;
const MAX_WEEKS = 44;

export function dubowitzToGestationalAge(totalScore: number): number {
  const weeks = Math.round(SLOPE * totalScore + INTERCEPT);
  return Math.max(MIN_WEEKS, Math.min(MAX_WEEKS, weeks));
}

export function sumDubowitzSection(section: Record<string, number | null> | undefined | null): number {
  if (!section) return 0;
  return Object.values(section).reduce<number>((sum, val) => sum + (Number(val) || 0), 0);
}

export function hasAnyDubowitzInput(scores: DubowitzScores | null | undefined): boolean {
  if (!scores) return false;
  const vals = [...Object.values(scores.neuro || {}), ...Object.values(scores.fisik || {})];
  return vals.some(v => v !== null && v !== undefined && (v as any) !== "");
}

export function gestationalChipColor(weeks: number | null): string {
  if (!weeks) return "grey";
  if (weeks < 28) return "red";
  if (weeks < 32) return "deep-orange";
  if (weeks < 37) return "amber darken-2";
  if (weeks <= 41) return "green";
  return "blue";
}

export function gestationalLabel(weeks: number | null): string {
  if (!weeks) return "-";
  if (weeks < 28) return "Extremely Preterm";
  if (weeks < 32) return "Very Preterm";
  if (weeks < 37) return "Moderate-Late Preterm";
  if (weeks <= 41) return "Aterm";
  return "Postterm";
}

export function emptyDubowitzScores(): DubowitzScores {
  const neuro: Record<string, number | null> = {};
  const fisik: Record<string, number | null> = {};
  DUBOWITZ_NEURO_ITEMS.forEach(i => { neuro[i.key] = null; });
  DUBOWITZ_FISIK_ITEMS.forEach(i => { fisik[i.key] = null; });
  return { neuro, fisik };
}
